// Per-page semantic SEO content: intro, conclusion, FAQs.
// Used by <SeoContentSection /> to add rich educational context,
// parent-focused information, and FAQPage JSON-LD for India search.

export type FAQ = { q: string; a: string };

export type PageSeo = {
  /** H2 above the intro paragraph(s). */
  introHeading: string;
  /** 1-3 short paragraphs (kid + parent friendly). */
  intro: string[];
  /** Optional bullet list of "What your child will learn". */
  learn?: string[];
  /** H2 above the FAQ block. */
  faqHeading?: string;
  faqs: FAQ[];
  /** Optional closing paragraph. */
  conclusion?: string;
};

export const pageSeo: Record<string, PageSeo> = {
  "/alphabet": {
    introHeading: "Alphabet & Numbers Learning for Preschool Kids",
    intro: [
      "TinyMinds Alphabet & Numbers helps Indian preschool and kindergarten kids learn the English ABCs and numbers 0–100 with phonics, tracing and playful audio. Each letter card uses real-life examples that little learners can recognise — like A for Apple, B for Ball and C for Cat.",
      "Designed for ages 3–8, the activities build letter recognition, number sense and early reading skills. Parents can use it as a daily 10-minute home learning routine alongside school.",
    ],
    learn: [
      "Letter recognition A–Z with phonic sounds",
      "Number recognition and counting 0–100",
      "Capital and small letter matching",
      "Tracing strokes for early writing readiness",
    ],
    faqs: [
      { q: "What age is alphabet learning best for?", a: "Children between 3 and 6 years are at the ideal age to start learning the alphabet. TinyMinds activities are simple enough for 3-year-olds and challenging enough to keep 6–8 year olds engaged." },
      { q: "Are the phonics sounds in Indian English?", a: "Yes. Sounds and example words are spoken in clear, neutral Indian English so kids in India can follow along easily." },
      { q: "Is the alphabet section free to use?", a: "Yes, all core ABC and number learning activities on TinyMinds are free for parents and children." },
      { q: "How long should my child practise each day?", a: "10–15 focused minutes a day is enough at this age. Short, regular sessions work better than long ones." },
    ],
    conclusion: "Daily practice with letters, sounds and numbers builds the strong foundation kids need for reading, writing and early math.",
  },

  "/english-learning": {
    introHeading: "English Learning for Kids in India",
    intro: [
      "TinyMinds English Learning is a complete early-English programme for kids ages 4–10. It covers grammar basics, sight words, vocabulary, sentence building and daily-use sentences — the core skills children need to read, write and speak English with confidence.",
      "Lessons are short, visual and interactive so children stay engaged while building real language skills used in Indian schools (CBSE, ICSE and State boards).",
    ],
    faqs: [
      { q: "Is TinyMinds good for CBSE / ICSE English?", a: "Yes. Topics like nouns, verbs, sight words and sentence building align with the early primary English syllabus followed by CBSE, ICSE and most State boards in India." },
      { q: "How does my child learn pronunciation?", a: "Every lesson includes audio in clear English so children hear correct pronunciation while they read." },
      { q: "Can a non-English-speaking parent help?", a: "Absolutely. The activities are self-guided — children can tap and listen, so parents do not need to read English aloud themselves." },
      { q: "How is this different from a normal English app?", a: "TinyMinds focuses on India-specific examples, daily use sentences and phonic-based learning, not just flashcards." },
    ],
    conclusion: "Strong English at an early age opens doors in school, reading and confidence — and it starts with daily, fun practice.",
  },

  "/math": {
    introHeading: "Fun Math Learning for Kids Ages 3–8",
    intro: [
      "TinyMinds Math turns numbers into a playground. Kids learn counting, addition, subtraction, shapes, patterns, time and money through colourful games, animations and quick quizzes.",
      "Each activity is designed for short attention spans, so children stay focused and actually enjoy practising math at home.",
    ],
    learn: [
      "Counting and number recognition 1–100",
      "Addition and subtraction within 20",
      "Shapes, sizes and patterns",
      "Telling time and basic money skills",
    ],
    faqs: [
      { q: "What math topics are covered?", a: "Counting, number sense, addition, subtraction, shapes, patterns, measurement, time and money — all aligned with early primary math." },
      { q: "My child says math is boring. Will this help?", a: "Yes. The games-first approach removes the pressure of worksheets, so kids practise without realising it's 'study'." },
      { q: "Can I track my child's progress?", a: "Yes — the dashboard shows what your child has practised and how they are improving over time." },
    ],
    conclusion: "A strong love for numbers in the early years makes school math feel easy for years to come.",
  },

  "/science": {
    introHeading: "Science Exploration for Curious Kids",
    intro: [
      "TinyMinds Science Lab introduces children ages 3–8 to the world around them — the human body, animals, plants, space, weather and simple experiments. Every topic is explained in child-friendly language with bright visuals and hands-on activities.",
      "The aim is to build curiosity first. When kids ask 'why?' and 'how?', they are already thinking like scientists.",
    ],
    learn: [
      "How the human body works",
      "Animals, plants and habitats",
      "Space, planets and the solar system",
      "Weather, seasons and nature cycles",
    ],
    faqs: [
      { q: "Are the experiments safe to do at home?", a: "Yes. All suggested experiments use safe, everyday household items and are designed to be done with a parent." },
      { q: "Which class / standard is this useful for?", a: "It supports EVS and Science learning from Nursery to Class 3 in Indian schools." },
      { q: "Do I need any special equipment?", a: "No. Most experiments use water, paper, kitchen items or things already at home." },
    ],
    conclusion: "Curiosity is the first step to science. Keep exploring, keep asking questions — every great scientist started exactly that way.",
  },

  "/geography": {
    introHeading: "Geography for Kids — Continents, Countries & Cultures",
    intro: [
      "TinyMinds Geography helps kids explore the world from home. Children discover continents, oceans, famous landmarks, flags, animals and cultures through interactive maps and games.",
      "It's perfect for primary-school learners in India who are starting to study EVS, Social Studies and General Knowledge.",
    ],
    faqs: [
      { q: "Does it cover Indian geography too?", a: "Yes. Asia and India are covered with maps, landmarks, animals and cultural facts kids can relate to." },
      { q: "Is this good for school GK quizzes?", a: "Definitely. Flags, capitals and landmarks featured here are commonly asked in school GK and quiz competitions." },
    ],
    conclusion: "Knowing the world makes children more curious, confident and globally aware from a young age.",
  },

  "/stories": {
    introHeading: "Bedtime Stories & Moral Stories for Kids",
    intro: [
      "TinyMinds Stories is a growing library of short, illustrated stories for children ages 3–8. From bedtime tales to moral stories and mini-adventures, every story is age-appropriate and easy to follow.",
      "Reading stories with your child every day builds vocabulary, imagination and emotional understanding.",
    ],
    faqs: [
      { q: "Are the stories suitable for bedtime?", a: "Yes. Stories are calm, positive and end on a gentle note — perfect for winding down before sleep." },
      { q: "Do the stories include morals?", a: "Many of them do. Stories cover values like honesty, kindness, sharing, courage and friendship." },
      { q: "Can my child read along?", a: "Yes. Stories use simple words and short sentences ideal for early readers and read-aloud sessions." },
    ],
    conclusion: "A story a day grows your child's vocabulary, imagination and love for reading — for life.",
  },

  "/games": {
    introHeading: "Educational Games for Kids Ages 3–8",
    intro: [
      "TinyMinds Educational Games turn learning into play. Memory games, matching, puzzles, math challenges and word games help children practise key skills without the pressure of a classroom.",
      "Every game is ad-free, safe and designed for small hands and short attention spans.",
    ],
    faqs: [
      { q: "Are these games safe for young children?", a: "Yes. All games are ad-free, contain no in-game purchases and are designed for kids ages 3–8." },
      { q: "Do the games actually teach something?", a: "Yes. Each game targets a real skill — memory, logic, counting, vocabulary or pattern recognition." },
    ],
    conclusion: "Play is how children learn best. A few minutes of focused play every day builds real skills.",
  },

  "/brain-teasers": {
    introHeading: "Brain Teasers & Logic Puzzles for Kids",
    intro: [
      "TinyMinds Brain Teasers help children develop logical thinking, problem-solving and patience through age-appropriate puzzles, riddles and pattern challenges.",
      "Perfect for kids who love a little challenge — and for parents who want to build thinking skills, not just memory.",
    ],
    faqs: [
      { q: "What skills do brain teasers build?", a: "Logical reasoning, attention to detail, problem-solving, patience and creative thinking." },
      { q: "What age are they suitable for?", a: "Most teasers work well for ages 4–8. Difficulty increases gradually so kids stay engaged." },
    ],
    conclusion: "A daily brain teaser is like a workout for your child's mind — small effort, big long-term gains.",
  },

  "/world-explorer": {
    introHeading: "World Explorer — Discover People, Places & Cultures",
    intro: [
      "TinyMinds World Explorer takes kids on a journey across continents to learn about people, festivals, food, animals and famous places.",
      "It's a fun, visual way to build general knowledge and global awareness from an early age.",
    ],
    faqs: [
      { q: "Is World Explorer different from Geography?", a: "Yes. Geography focuses on maps and locations; World Explorer focuses on cultures, people and everyday life around the world." },
    ],
  },

  "/spellings": {
    introHeading: "Spellings of the Day for Kids",
    intro: [
      "Build your child's spelling skills one word a day. TinyMinds Spellings of the Day features age-appropriate words with audio pronunciation, meaning and example sentences.",
      "Daily exposure to new words is one of the most effective ways to grow vocabulary and improve writing.",
    ],
    faqs: [
      { q: "How often are new words added?", a: "A new word is featured every day, with an archive of past words you can revisit any time." },
      { q: "What age is this suitable for?", a: "Best for ages 5–10, but younger children can also enjoy listening to the word and meaning with a parent." },
    ],
    conclusion: "One new word a day adds up to hundreds of new words a year — without it ever feeling like homework.",
  },

  "/sentences-of-the-day": {
    introHeading: "Sentences of the Day — Daily English Practice",
    intro: [
      "Help your child speak and write better English with one new sentence every day. Each sentence comes with audio, meaning and a simple example so kids understand how it's used in real life.",
    ],
    faqs: [
      { q: "How does this help my child speak English?", a: "Hearing and repeating natural sentences daily builds confidence, fluency and the right sentence structure." },
      { q: "Is this useful for school exams?", a: "Yes. Many sentences mirror the kind of language used in early primary English textbooks." },
    ],
  },

  "/": {
    introHeading: "TinyMinds — Trusted Early Learning App for Kids in India",
    intro: [
      "TinyMinds is an India-made early learning platform for children ages 3 to 8. We help preschool, LKG, UKG and early primary kids learn through fun games, phonics, stories, rhymes, math, science, worksheets and brain-development activities — all in one safe, ad-free place.",
      "Built for Indian parents and teachers, TinyMinds blends English learning with India-specific examples, festivals, animals and culture so children feel at home while they learn. Lessons follow the spirit of CBSE, ICSE and State board early-primary syllabi, while staying playful and pressure-free.",
      "From bedtime stories and moral tales to phonics, counting and printable worksheets, every activity is designed to build curiosity, confidence and strong learning habits for the long term.",
    ],
    learn: [
      "Alphabet, phonics and early reading",
      "Numbers, counting and early math",
      "Stories, rhymes and moral tales for kids",
      "Science, geography and general knowledge",
      "Brain teasers and logical thinking",
      "Free printable worksheets and activities",
    ],
    faqs: [
      { q: "Is TinyMinds free for Indian parents?", a: "Yes. Most learning activities, stories, rhymes and games on TinyMinds are completely free for parents and teachers in India. Premium printables and advanced content are available with an affordable plan." },
      { q: "Which age group is TinyMinds best for?", a: "TinyMinds is designed for children ages 3 to 8 — that covers playgroup, nursery, LKG, UKG and early primary (Class 1–2)." },
      { q: "Does TinyMinds support CBSE, ICSE and State board kids?", a: "Yes. Our topics — phonics, sight words, early math, EVS, science basics, GK and moral stories — match what kids learn in CBSE, ICSE and most State board schools in India." },
      { q: "Is TinyMinds safe and ad-free for young children?", a: "Yes. TinyMinds has no third-party ads, no in-app gambling-style rewards and no unsafe outbound links. It is built specifically for young Indian learners." },
      { q: "Can teachers use TinyMinds in preschools and kindergartens?", a: "Absolutely. Many activities, printables and rhymes are ideal for classroom use in preschools, playschools and kindergartens across India." },
      { q: "क्या TinyMinds हिंदी में उपलब्ध है? / Is TinyMinds available in Hindi?", a: "Right now TinyMinds teaches mainly in clear Indian English so kids build strong English skills early. Hindi support and bilingual stories are on our roadmap." },
    ],
    conclusion: "TinyMinds is built in India, for India — to make early learning joyful, trusted and within reach of every parent and teacher.",
  },

  "/contact": {
    introHeading: "Get in Touch with TinyMinds",
    intro: [
      "Have a question about our activities, a suggestion for new content, or need help with your account or premium plan? The TinyMinds team is happy to help parents, teachers and schools across India and beyond.",
      "We usually respond within 1–2 working days. For faster help, please include your child's age group and the page or feature you have a question about.",
    ],
    faqHeading: "Frequently Asked Questions",
    faqs: [
      { q: "How quickly will I get a reply?", a: "We reply to most messages within 1–2 working days (Monday to Friday). Premium support requests are prioritised." },
      { q: "I forgot my password or can't log in. What should I do?", a: "Use the 'Forgot password' link on the login screen to reset your password by email. If you still can't access your account, write to us from the email you used to sign up and we'll help you recover it." },
      { q: "How do I upgrade to TinyMinds Premium?", a: "Visit the Pricing page from the top menu, choose a plan, and complete the secure checkout. Premium unlocks printables, full stories and advanced learning activities instantly." },
      { q: "I paid but premium features are still locked. What now?", a: "Premium usually activates within a minute of payment. If it doesn't, please log out and log back in. If it's still locked, contact us with your payment reference and we'll fix it the same day." },
      { q: "Can I request new topics, worksheets or stories?", a: "Yes! We love content requests from parents and teachers. Tell us the topic and your child's age group and we'll add it to our content roadmap." },
      { q: "Do you offer support for schools or bulk plans?", a: "Yes. Schools, preschools and tuition centres can contact us for bulk pricing and a custom onboarding for teachers." },
      { q: "How do I report a bug or wrong information?", a: "Please send us the page link and a short description (or screenshot if possible). Reporting issues helps us keep TinyMinds accurate and safe for every child." },
    ],
    conclusion: "Your feedback shapes TinyMinds. Whether it's a kind word, a question or a content idea — we'd love to hear from you.",
  },
};

export function getPageSeo(path: string): PageSeo | undefined {
  return pageSeo[path];
}
